<script lang="ts" setup>
  import { ref, type PropType } from 'vue';
  import { useBreakpoints } from '@storefront/storefront';
  import type { Pagination } from '@storefront/types';
  import { StoreLayout, AccountLayout } from '@storefront/components';
  import { useHttpClient, useConfirmation, useRoute, useTranslator } from '@othercommerce/foundation';
  import type { WishlistResource } from '@storefront/resources';

  defineOptions({
    layout: [StoreLayout, AccountLayout],
    inheritAttrs: false,
  });

  defineProps({
    wishlists: { type: Object as PropType<Pagination<WishlistResource>>, required: true },
  });

  const confirm = useConfirmation();
  const breakpoints = useBreakpoints();
  const open = ref(false);
  const http = useHttpClient();
  const route = useRoute();
  const translator = useTranslator();

  function onDestroy(wishlist: WishlistResource) {
    confirm({
      callback: () => http.delete(route('web.account.wishlist.destroy', { wishlist })),
      title: translator.trans('SHOP.ACCOUNT.HEADER.WISHLIST_DESTROY'),
      description: translator.trans('SHOP.ACCOUNT.MESSAGE.WISHLIST_DESTROY'),
      yes: translator.trans('SHOP.ACTION.DESTROY'),
      no: translator.trans('SHOP.ACTION.CANCEL'),
    }).catch(() => void (0));
  }
</script>
<template>
  <AccountMobileNavigation :href="$route('web.account.show')">
    {{ $t('SHOP.ACCOUNT.HEADER.WISHLISTS') }}
  </AccountMobileNavigation>
  <SfTile class="account-wishlist" large>
    <template v-if="breakpoints.lg" #title>
      <h1 :aria-hidden="!breakpoints.lg" :class="{'hide': !breakpoints.lg }" class="account__heading">
        {{ $t('SHOP.ACCOUNT.HEADER.WISHLISTS') }}
      </h1>
    </template>
    <template #default>
      <div v-if="wishlists.data.length > 0" class="account-wishlist__products">
        <SfListCard
          v-for="wishlist in wishlists.data"
          :key="wishlist.id"
          class="account-wishlist__product"
          interactive
          role="button"
          tabindex="0"
          @click="http.get($route('web.account.wishlist.show', {wishlist: wishlist.id}))"
          @keydown.enter="http.get($route('web.account.wishlist.show', {wishlist: wishlist.id}))"
        >
          <template #default>
            <header class="account-wishlist__product-content">
              <h2 class="account-wishlist__product-title">{{ wishlist.name }}</h2>
              <p class="account-wishlist__product-count">{{ $tc('SHOP.LABEL.PRODUCTS_COUNT', wishlist.variantsCount, { count: wishlist.variantsCount }) }}</p>
            </header>
          </template>
          <template #icons>
            <SfButton :title="$t('SHOP.ACTION.DESTROY')" danger rounded square transparent @click.stop="onDestroy(wishlist)">
              <template #after>
                <SfIconTrash aria-hidden="true" />
              </template>
            </SfButton>
          </template>
        </SfListCard>
      </div>
      <SfDataPlaceholder v-else>
        {{ $t('SHOP.ACCOUNT.MESSAGE.WISHLIST_EMPTY') }}
      </SfDataPlaceholder>
    </template>
    <template #footer>
      <SfButton primary @click="open = true">
        <template #before>
          <SfIconPlus aria-hidden="true" />
        </template>
        <template #default>
          {{ $t('SHOP.ACTION.CREATE') }}
        </template>
      </SfButton>
    </template>
  </SfTile>
  <SfPagination :pagination="wishlists" />
  <WishlistEditModal v-model:open="open" @saved="open = false" />

  <RouterView />
</template>
<style lang="scss">
  @use "@storefront/styles/mixins/media";

  .account-wishlist {
    &__products {
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }

    &__product {
      cursor: pointer;
    }
  }

  @include media.query(lg) {
    .account-wishlist {
      &__product-title {
        font-size: 1.125rem;
      }

      &__product-count {
        font-size: 0.875rem;
      }
    }
  }
</style>
